const API_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

export type UserRole = 'CUSTOMER' | 'COLLABORATOR' | 'AGENCY' | 'VENDOR' | 'COMPANY' | 'FLEET_OWNER' | 'ADMIN' | 'SERVICE_PROVIDER';

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  email_verified?: boolean;
  vendor_status?: 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED' | null;
  vendor_approved_at?: string | null;
  commission_rate?: number | null;
  company_name?: string | null;
  company_vat?: string | null;
  company_pec?: string | null;
  company_sdi?: string | null;
  vendor_bio?: string | null;
  vendor_phone?: string | null;
  payout_iban?: string | null;
  payout_holder?: string | null;
  can_book_as_guest?: boolean;
  can_bypass_landing?: boolean;
  provides_excursions?: boolean;
  company_services?: string[] | null;
  /** Ground truth counts (charter DB) per dual-mode partner.* switcher. */
  boats_count?: number;
  excursions_count?: number;
  is_suspended?: boolean;
  suspension_reason?: string | null;
  max_boats?: number | null;
  company_logo_url?: string | null;
  payment_collection_mode?: 'full' | 'commission_only';
  payout_frequency?: 'weekly' | 'biweekly' | 'monthly';
  phone_prefix?: string | null;
  phone_number?: string | null;
  phone_verified_at?: string | null;
  phone_verified_method?: 'sms' | 'whatsapp' | null;
  kyc_verified_at?: string | null;
}

export interface ImpersonationInfo {
  admin_id: string;
  admin_email: string;
  admin_name: string;
}

export interface RecentImpersonatedUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  company_name: string | null;
  last_impersonated_at: string;
}

export const authService = {
  async register(email: string, password: string, name: string, role: UserRole = 'CUSTOMER', extra?: {
    company_name?: string;
    company_vat?: string;
    company_pec?: string;
    company_sdi?: string;
    vendor_phone?: string;
    invitation_token?: string;
    invite_code?: string;
    referral_source?: string;
    [k: string]: string | boolean | undefined | null;
  }) {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name, role, ...extra })
    });

    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Errore del server: risposta non valida');
    }

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || data.message || 'Registrazione fallita');
    }

    return data;
  },

  async login(email: string, password: string, totpToken?: string) {
    const reqBody: Record<string, string> = { email, password };
    if (totpToken) reqBody.totp_token = totpToken;
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(reqBody)
    });

    const contentType = res.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error('Errore del server: risposta non valida');
    }

    const data = await res.json();

    if (!res.ok) {
      const err = new Error(data.error || data.message || 'Login fallito') as Error & { code?: string };
      err.code = data.code;
      throw err;
    }

    return data;
  },

  async logout() {
    try {
      const res = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      return res.ok;
    } catch (error) {
      console.error('Logout error:', error);
      return false;
    }
  },

  async getCurrentUser(): Promise<User | null> {
    const res = await this.getMe();
    return res.user;
  },

  async getMe(): Promise<{ user: User | null; impersonating: ImpersonationInfo | null }> {
    try {
      const res = await fetch(`${API_URL}/auth/me`, {
        credentials: 'include'
      });

      if (!res.ok) {
        if (res.status === 401) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            return this.getMe();
          }
        }
        return { user: null, impersonating: null };
      }

      const data = await res.json();
      return { user: data.user || null, impersonating: data.impersonating || null };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, impersonating: null };
    }
  },

  async startImpersonate(targetUserId: string): Promise<{ id: string; email: string; name: string; role: string }> {
    const res = await fetch(`${API_URL}/admin/users/${targetUserId}/impersonate`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Impossibile avviare impersonazione');
    }
    return data.data.target;
  },

  async stopImpersonate(): Promise<void> {
    const res = await fetch(`${API_URL}/admin/stop-impersonate`, {
      method: 'POST',
      credentials: 'include',
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Impossibile terminare impersonazione');
    }
  },

  async getImpersonateRecent(): Promise<RecentImpersonatedUser[]> {
    const res = await fetch(`${API_URL}/admin/impersonate-recent`, {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return [];
    const data = await res.json();
    return data.success ? data.data : [];
  },

  async refreshToken() {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });
      return res.ok;
    } catch (error) {
      console.error('Refresh token error:', error);
      return false;
    }
  },

  async forgotPassword(email: string) {
    const res = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Errore durante la richiesta');
    return data;
  },

  async resetPassword(token: string, password: string) {
    const res = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Errore durante il reset');
    return data;
  }
};

export default authService;
